import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dayShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

/**
 * Compact 14-day line chart of daily views vs submissions for a form.
 * Fetches once when it mounts (i.e. when the row is expanded).
 */
const FormFunnelTimeseries = ({ formId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!formId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/lead-forms/${formId}/timeseries?days=14`, { withCredentials: true })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [formId]);

  if (loading) return <div className="h-32 flex items-center justify-center text-xs text-slate-400">Loading chart…</div>;
  if (!data || !data.series?.length) return <div className="h-32 flex items-center justify-center text-xs text-slate-400">No data.</div>;

  const totalViews = data.series.reduce((s, r) => s + r.views, 0);
  const totalSubs = data.series.reduce((s, r) => s + r.submissions, 0);

  return (
    <div className="space-y-2" data-testid={`funnel-timeseries-${formId}`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium text-slate-600 flex items-center gap-1">
          <TrendingUp className="h-3 w-3 text-blue-700" /> Last {data.days} days
        </p>
        <p className="text-[10px] text-slate-500">
          <span className="text-blue-700 font-semibold">{totalViews}</span> views ·{' '}
          <span className="text-emerald-700 font-semibold">{totalSubs}</span> submits
        </p>
      </div>
      <div style={{ height: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.series} margin={{ top: 6, right: 8, bottom: 4, left: -22 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="day" tickFormatter={dayShort} tick={{ fontSize: 10, fill: '#64748b' }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
            <Tooltip
              labelFormatter={(v) => dayShort(v)}
              contentStyle={{ fontSize: 11, padding: '4px 6px', borderRadius: 6 }}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line type="monotone" dataKey="views" stroke="#1d4ed8" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="submissions" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FormFunnelTimeseries;
